// src/components/Layout.js v1.5
import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getGhostCars, getExpiringTenants, APP_VERSION } from '../database/db';
import { LayoutDashboard, Users, Building2, BookOpen, BarChart2, Settings, Power, Menu, X, Clock, DollarSign, Car } from 'lucide-react';
import { addCashCheckpoint } from '../database/db';
import { connectPrinter, getSavedPrinter, clearSavedPrinter, setPrinterDevice, getPrinterDevice } from '../utils/bluetooth-printer';

const NAV = [
  { path:'/dashboard', icon:LayoutDashboard, label:'Dashboard',      roles:['admin','staff','supervisor'] },
  { path:'/tenants',   icon:Users,           label:'Tenants',        roles:['admin','supervisor'] },
  { path:'/mou',       icon:Building2,       label:'MOU Companies',  roles:['admin','supervisor'] },
  { path:'/accounting',icon:BookOpen,        label:'Accounting',     roles:['admin'] },
  { path:'/reports',   icon:BarChart2,       label:'Reports',        roles:['admin','supervisor','staff'] },
  { path:'/settings',  icon:Settings,        label:'Settings',       roles:['admin'] },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [alerts, setAlerts] = useState({ ghosts:0, expiring:0 });
  const [time, setTime] = useState(new Date());
  const [showCheckpoint, setShowCheckpoint] = useState(false);
  const [showPrinter, setShowPrinter] = useState(false);
  const [printerStatus, setPrinterStatus] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [checkpointForm, setCheckpointForm] = useState({ amount:'', type:'shift-handover', note:'' });

  useEffect(() => { const t = setInterval(()=>setTime(new Date()),1000); return ()=>clearInterval(t); }, []);
  useEffect(() => { setAlerts({ ghosts:getGhostCars().length, expiring:getExpiringTenants(2).length }); }, []);

  const handleLogout = () => { logout(); navigate('/login'); };
  const visibleNav = NAV.filter(n => n.roles.includes(user?.role));

  const saveCheckpoint = () => {
    if (!checkpointForm.amount || parseFloat(checkpointForm.amount) < 0) return;
    addCashCheckpoint({ amount:parseFloat(checkpointForm.amount), type:checkpointForm.type, note:checkpointForm.note, staffId:user.id, staffName:user.name });
    setShowCheckpoint(false);
    setCheckpointForm({ amount:'', type:'shift-handover', note:'' });
  };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100vh', overflow:'hidden' }}>

      {/* TOP BAR */}
      <div style={{ minHeight:52, flexShrink:0, background:'var(--bg-card)', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', flexWrap:'nowrap', padding:'6px 10px', gap:8, zIndex:100 }}>
        <button onClick={()=>setSidebarOpen(o=>!o)} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--accent)', display:'flex', alignItems:'center', padding:6, borderRadius:6, flexShrink:0 }}>
          {sidebarOpen ? <X size={18}/> : <Menu size={18}/>}
        </button>
        <div style={{ flex:1 }}>
          <div style={{ fontFamily:'var(--font-display)', fontSize:15, fontWeight:800, lineHeight:1.1 }}>
            <span style={{ color:'var(--accent)' }}>PARK</span><span style={{ color:'var(--text-primary)' }}>SMART </span><span style={{ color:'var(--accent)', fontSize:11 }}>PRO</span>
          </div>
          <div style={{ fontSize:9, color:'var(--text-muted)', fontFamily:'var(--font-mono)', letterSpacing:'0.08em', textTransform:'uppercase' }}>Al Barakah Car Parking</div>
        </div>
        <button
          onClick={()=>{navigate('/dashboard');setSidebarOpen(false);}}
          style={{ background:'var(--accent-dim)', border:'1px solid var(--accent)', borderRadius:'var(--radius-sm)', padding:'6px 14px', cursor:'pointer', color:'var(--accent)', fontFamily:'var(--font-display)', fontWeight:700, fontSize:13, flexShrink:0 }}
        >
          🅿️ Dashboard
        </button>
        <div className="flex-row" style={{ gap:6 }}>
          <div className="live-dot"/>
          <span style={{ fontSize:11, fontFamily:'var(--font-mono)', color:'var(--text-muted)' }}>
            {time.toLocaleTimeString('en-AE',{hour:'2-digit',minute:'2-digit',second:'2-digit'})}
          </span>
        </div>
      </div>

      {/* BODY */}
      <div style={{ display:'flex', flex:1, overflow:'hidden' }}>

        {/* SIDEBAR */}
        {sidebarOpen && (
          <aside style={{ width:220, minWidth:220, background:'var(--bg-card)', borderRight:'1px solid var(--border)', display:'flex', flexDirection:'column', overflowY:'auto', flexShrink:0 }}>
            <div style={{ padding:'10px 14px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', gap:8 }}>
              <div className="user-avatar">{user?.name?.[0]?.toUpperCase()||'U'}</div>
              <div style={{ flex:1, minWidth:0 }}>
                <div className="user-name">{user?.name}</div>
                <div className="user-role">{user?.role}</div>
              </div>
            </div>

            <nav style={{ flex:1, padding:'6px 0' }}>
              <div className="nav-section-label">Menu</div>
              {visibleNav.map(item => (
                <NavLink key={item.path} to={item.path} className={({isActive})=>`nav-item${isActive?' active':''}`} onClick={()=>setSidebarOpen(false)}>
                  <item.icon size={15} className="nav-icon"/>
                  {item.label}
                  {item.path==='/dashboard' && alerts.ghosts>0 && <span className="nav-badge">{alerts.ghosts}</span>}
                  {item.path==='/tenants' && alerts.expiring>0 && <span className="nav-badge">{alerts.expiring}</span>}
                </NavLink>
              ))}

              {/* SHIFT SECTION */}
              <div className="nav-section-label" style={{ marginTop:8 }}>Shift</div>
              {(user?.role==='staff'||user?.role==='supervisor'||user?.role==='admin') && (
                <NavLink to="/shift-close" className={({isActive})=>`nav-item${isActive?' active':''}`} onClick={()=>setSidebarOpen(false)}>
                  <Clock size={15} className="nav-icon"/>Close Shift
                </NavLink>
              )}
              {/* Car History quick access */}
              <NavLink to="/car-history" className={({isActive})=>`nav-item${isActive?' active':''}`} onClick={()=>setSidebarOpen(false)}>
                <Car size={15} className="nav-icon"/>Car History
              </NavLink>
              {/* Printer - visible to ALL roles */}
              <button className="nav-item" onClick={()=>{setSidebarOpen(false);setShowPrinter(true);}}>
                <span style={{fontSize:15,marginRight:2}}>🖨️</span>Printer
              </button>
              {/* Cash Checkpoint */}
              {(user?.role==='supervisor'||user?.role==='admin') && (
                <button className="nav-item" onClick={()=>{setSidebarOpen(false);setShowCheckpoint(true);}}>
                  <DollarSign size={15} className="nav-icon"/>Cash Checkpoint
                </button>
              )}
            </nav>

            <div style={{ padding:'10px 14px', borderTop:'1px solid var(--border)' }}>
              <button className="btn btn-ghost btn-full btn-sm" onClick={handleLogout} style={{ justifyContent:'flex-start', gap:8 }}>
                <Power size={13}/> Sign Out
              </button>
              <div className="version-tag">{APP_VERSION} · ParkSmart Pro</div>
            </div>
          </aside>
        )}

        {/* MAIN */}
        <div style={{ flex:1, overflowY:'auto', overflowX:'hidden', background:'var(--bg-base)', display:'flex', flexDirection:'column', minWidth:0 }}>
          <Outlet/>
        </div>
      </div>

      {/* CASH CHECKPOINT MODAL */}
      {showCheckpoint && (
        <div className="modal-overlay" onClick={()=>setShowCheckpoint(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header"><div className="modal-title">💰 Cash Checkpoint</div><button className="btn btn-ghost btn-sm" onClick={()=>setShowCheckpoint(false)}>✕</button></div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Checkpoint Type</label>
                <select className="form-select" value={checkpointForm.type} onChange={e=>setCheckpointForm({...checkpointForm,type:e.target.value})}>
                  <option value="shift-start">Shift Start — Taking over cash</option>
                  <option value="shift-handover">Shift Handover — Passing cash</option>
                  <option value="admin-pickup">Admin Cash Pickup</option>
                  <option value="break-start">Break Start</option>
                  <option value="break-end">Break End</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Cash Amount (AED) *</label>
                <input className="form-input" type="number" placeholder="0.00" value={checkpointForm.amount} onChange={e=>setCheckpointForm({...checkpointForm,amount:e.target.value})} style={{ fontSize:22, fontWeight:700, textAlign:'center', fontFamily:'var(--font-mono)' }}/>
              </div>
              <div className="form-group">
                <label className="form-label">Note (optional)</label>
                <input className="form-input" placeholder="e.g. Handed to Ahmed" value={checkpointForm.note} onChange={e=>setCheckpointForm({...checkpointForm,note:e.target.value})}/>
              </div>
              <div style={{ background:'var(--bg-input)', borderRadius:'var(--radius-sm)', padding:'10px 12px', fontSize:12, color:'var(--text-secondary)' }}>
                Staff: <strong>{user?.name}</strong> · {new Date().toLocaleTimeString('en-AE',{hour:'2-digit',minute:'2-digit'})}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-outline" onClick={()=>setShowCheckpoint(false)}>Cancel</button>
              <button className="btn btn-success" onClick={saveCheckpoint}>✅ Save</button>
            </div>
          </div>
        </div>
      )}
    {/* PRINTER MODAL - accessible to all roles */}
      {showPrinter && (
        <div className="modal-overlay" onClick={()=>setShowPrinter(false)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-title">🖨️ Bluetooth Printer</div>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowPrinter(false)}>✕</button>
            </div>
            <div className="modal-body">
              {getSavedPrinter()
                ? <div>
                    <div style={{background:'var(--success-dim)',border:'1px solid var(--success)',borderRadius:'var(--radius-sm)',padding:'12px 14px',marginBottom:14}}>
                      <div style={{fontWeight:700,color:'var(--success)',fontSize:15}}>✅ Printer Connected</div>
                      <div style={{fontSize:13,color:'var(--text-secondary)',marginTop:4}}>Name: <strong>{getSavedPrinter().name}</strong></div>
                    </div>
                    <div style={{fontSize:13,color:'var(--text-muted)',marginBottom:12}}>Tap Reconnect if printer was turned off and back on.</div>
                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
                      <button className="btn btn-outline" onClick={()=>{clearSavedPrinter();setPrinterDevice(null);setPrinterStatus('Printer removed.');}}>🗑️ Remove</button>
                      <button className="btn btn-primary" disabled={connecting} onClick={async()=>{
                        setConnecting(true);setPrinterStatus('');
                        try{const d=await connectPrinter();setPrinterDevice(d);setPrinterStatus('✅ Reconnected: '+d.name);}
                        catch(e){setPrinterStatus('❌ '+e.message);}
                        setConnecting(false);
                      }}>{connecting?'Connecting...':'🔄 Reconnect'}</button>
                    </div>
                  </div>
                : <div>
                    <div style={{fontSize:14,color:'var(--text-muted)',marginBottom:16,lineHeight:1.7}}>
                      No printer connected.<br/>
                      1. Turn on printer<br/>
                      2. Pair <strong>Printer001</strong> in Android Bluetooth settings<br/>
                      3. Tap Connect below
                    </div>
                    <button className="btn btn-success btn-full btn-lg" disabled={connecting} onClick={async()=>{
                      setConnecting(true);setPrinterStatus('');
                      try{const d=await connectPrinter();setPrinterDevice(d);setPrinterStatus('✅ Connected: '+d.name);}
                      catch(e){setPrinterStatus('❌ '+e.message);}
                      setConnecting(false);
                    }}>{connecting?'⏳ Connecting...':'🖨️ Connect Printer'}</button>
                  </div>
              }
              {printerStatus&&<div className={`alert ${printerStatus.startsWith('✅')?'alert-success':'alert-danger'}`} style={{marginTop:12}}>{printerStatus}</div>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
